import { AuthProviderProps } from "react-oidc-context";
import { OidcClient, OidcClientSettings, SigninResponse, UseRefreshTokenArgs, UserManager, UserManagerSettings } from "oidc-client-ts";
import { produce } from "immer";
import { Config, Provider, useConfigContext } from "@imminently/react/context/config";
import { getUser } from "@imminently/react/store/oidc";

export const uri = (path: string) =>
  `${window.location.protocol}//${window.location.host}${path}`;


interface AzureSettings {
  isB2C: boolean;
}

export type AzureConfig = Config & {
  auth: {
    isB2C: boolean;
  }
};

/**
 * A custom override of the useRefreshToken method to support sending the redirect_uri.
 * Ideally the oidc-client-ts library would support this out of the box, but it does not.
 * We will be submitting a change request to the library to support this.
 */
class AzureOidcClient extends OidcClient {
  isB2C = false;

  constructor(settings: OidcClientSettings & AzureSettings) {
    super(settings);
    this.isB2C = settings.isB2C;
  }

  async useRefreshToken({
    state,
    timeoutInSeconds,
  }: UseRefreshTokenArgs): Promise<SigninResponse> {
    const logger = this._logger.create("useAzureRefreshToken");

    // https://github.com/authts/oidc-client-ts/issues/695
    // In some cases (e.g. AzureAD), not all granted scopes are allowed on token refresh requests.
    // Therefore, we filter all granted scopes by a list of allowable scopes.
    let scope;
    if (this.settings.refreshTokenAllowedScope === undefined) {
      scope = state.scope;
    } else {
      const allowableScopes = this.settings.refreshTokenAllowedScope.split(" ");
      const providedScopes = state.scope?.split(" ") || [];

      scope = providedScopes.filter(s => allowableScopes.includes(s)).join(" ");
    }

    // The value urn:ietf:wg:oauth:2.0:oob is used to specify that the authorization response should be
    // displayed in the user agent (e.g. web browser) and not sent to a redirect URL
    const redirect_uri = this.isB2C ? "urn:ietf:wg:oauth:2.0:oob" : undefined;

    const result = await this._tokenClient.exchangeRefreshToken({
      refresh_token: state.refresh_token,
      // provide the (possible filtered) scope list
      scope,
      timeoutInSeconds,
      // @ts-ignore - provide the redirect_uri as its required for Azure AD B2C
      redirect_uri,
    });

    const response = new SigninResponse(new URLSearchParams());
    Object.assign(response, result);
    logger.debug("validating response", response);
    await this._validator.validateRefreshResponse(response, {
      ...state,
      // overide the scope in the state handed over to the validator
      // so it can set the granted scope to the requested scope in case none is included in the response
      scope,
    });
    return response;
  }
}

class AzureUserManager extends UserManager {
  constructor(config: UserManagerSettings & AzureSettings) {
    super(config);
    // @ts-ignore
    this._client = new AzureOidcClient(config);
  }
}

/** Modifies the raw data from a standard provider to support Azure AD/B2C */
export const azureProvider = (provider: Provider): Provider => () => {
  // @ts-ignore - __ENV__ is a custom property added by imminently platform
  const raw: Record<string, string> = provider() ?? window.__ENV__ ?? {};
  if (!raw) return raw;
  // use immer produce as we don't want to mutate the original object
  return produce(raw, (config) => {
    if (config["AUTH_TYPE"] === "azure-b2c") {
      // @ts-ignore TODO allow assigning booleans in the type
      config["AUTH_IS_B2C"] = true;
    } else {
      // @ts-ignore TODO allow assigning booleans in the type
      config["AUTH_IS_B2C"] = false;
      if (!config["AUTH_ENDPOINT"].includes("/v2.0")) {
      // its plain Azure AD, add 2.0 endpoint
        const suffix = config["AUTH_ENDPOINT"].endsWith("/") ? "v2.0" : "/v2.0";
        config["AUTH_ENDPOINT"] = (config["AUTH_ENDPOINT"] + suffix);
      }
    }
    return config as Record<string, string>;
  });
};

export const azureAuthBearerHeader = (auth: AzureConfig["auth"]) => {
  const user = getUser(auth.authority, auth.clientId);
  if (user) {
    const token = auth.isB2C ? user.id_token : user.access_token;
    if (!token) {
      console.warn("[AzureAuth] User found but no token available", { 
        isB2C: auth.isB2C, 
        hasIdToken: !!user.id_token, 
        hasAccessToken: !!user.access_token,
        expired: user.expired 
      });
      return {};
    }
    return {
      Authorization: `Bearer ${token}`,
    } as Record<string, string>;
  }

  console.warn("[AzureAuth] No user found in storage", { 
    authority: auth.authority, 
    clientId: auth.clientId 
  });
  return {};
};

export const useAzureAuthConfig = (redirect = "/authcallback") => {
  const { auth } = useConfigContext() as AzureConfig;

  const settings = {
    client_id: auth.clientId,
    authority: auth.authority,
    redirect_uri: uri(redirect),
    post_logout_redirect_uri: uri("/"),
    scope: auth.isB2C ? undefined : `openid profile ${auth.scope}`,
    isB2C: auth.isB2C,
    automaticSilentRenew: true,
    silent_redirect_uri: uri("/authcallback"),
    includeIdTokenInSilentRenew: true,
  } as UserManagerSettings & AzureSettings;

  const userManager = new AzureUserManager(settings);
  return { userManager } as AuthProviderProps;
};
